import { UseFormReturn } from 'react-hook-form';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'shared/components/alerts/use-alert';
import { useUpdateWorkerAvailability } from './use-update-worker-availability';
import { useCreateAvailability } from './use-create-availability';
import { AvailabilityMode } from './use-availability-action-handler';
import { SegmentsForEdit, TimeSlotFormContext } from '../wizard/time-slot.types';
import { isOverlappingSegment } from '../utils/is-overlapping-segment';

type Props = {
  workerId: string | undefined;
  formMethods: UseFormReturn<TimeSlotFormContext, any, undefined>;
  originalAvailability: SegmentsForEdit[];
  availabilityDate: DateTime;
  fromDate: DateTime;
  searchTerm: string | undefined;
  cancel: () => void;
  mode?: AvailabilityMode;
};
export const useHandleAvailabilitySubmission = ({
  workerId,
  formMethods,
  originalAvailability,
  availabilityDate,
  fromDate,
  searchTerm,
  cancel,
  mode = 'edit',
}: Props) => {
  const { t } = useTranslation('all-platforms-common');
  const isCreateMode = mode === 'create';
  const { alertSuccess, alertError } = useAlert();
  const { handleSubmit: formHandleSubmit, watch, setError } = formMethods;
  const availabilitySegments = watch('availability');

  const { updateWorkerAvailability } = useUpdateWorkerAvailability(
    workerId ?? '',
    fromDate,
    searchTerm,
    cancel
  );
  const { createAvailabilityMutation } = useCreateAvailability(searchTerm, fromDate, 'ogp');

  const handleSubmit = formHandleSubmit((data: TimeSlotFormContext) => {
    const overlappingIndex = data.availability.findIndex((segment) =>
      isOverlappingSegment(segment, [...data.availability, ...originalAvailability])
    );
    const internallyInvalidIndex = data.availability.findIndex(
      (segment) =>
        DateTime.fromFormat(segment.utcStartDateTime, 'T') >=
        DateTime.fromFormat(segment.utcEndDateTime, 'T')
    );

    if (internallyInvalidIndex !== -1) {
      setError(`availability.${internallyInvalidIndex}.utcStartDateTime`, {
        message: t('edit_availability.timeslot_invalid'),
      });
      setError(`availability.${internallyInvalidIndex}.utcEndDateTime`, {
        message: t('edit_availability.timeslot_invalid'),
      });
      return;
    }
    if (overlappingIndex !== -1) {
      setError(`availability.${overlappingIndex}.utcStartDateTime`, {
        message: t('edit_availability.timeslot_overlap'),
      });
      setError(`availability.${overlappingIndex}.utcEndDateTime`, {
        message: t('edit_availability.timeslot_overlap'),
      });
      return;
    }

    if (isCreateMode) {
      if (!data.workerOptions || data.workerOptions.length === 0) {
        setError('workerOptions', {
          message: t('edit_availability.worker_required') || 'Please select a worker',
        });
        return;
      }

      const segmentsToCreate = data.availability
        .filter((segment) => segment.utcStartDateTime && segment.utcEndDateTime)
        .map((segment) => ({
          startDateTime: DateTime.fromFormat(segment.utcStartDateTime, 'T').toISO(),
          endDateTime: DateTime.fromFormat(segment.utcEndDateTime, 'T').toISO(),
          availabilityDate: availabilityDate.toISO(),
        }));

      if (segmentsToCreate.length === 0) {
        setError('availability', {
          message: t('edit_availability.timeslot_required') || 'Please add at least one time slot',
        });
        return;
      }

      const selectedWorker = data.workerOptions[0];
      createAvailabilityMutation({
        workerId: selectedWorker.id,
        updatedSegments: [],
        removedSegmentIds: [],
        newSegments: segmentsToCreate,
      })
        .then(() => {
          alertSuccess(
            t('edit_availability.create_success') || t('edit_availability.update_success')
          );
          cancel();
        })
        .catch((error) => {
          alertError(error);
        });
    } else {
      if (!workerId) {
        return;
      }
      updateWorkerAvailability({
        updatedSegments: data.availability.map((segment) => ({
          segmentId: segment.segmentId,
          startDateTime: DateTime.fromFormat(segment.utcStartDateTime, 'T').toISO(),
          endDateTime: DateTime.fromFormat(segment.utcEndDateTime, 'T').toISO(),
          availabilityDate: availabilityDate.toISO(),
        })),
        //TODO: Adding proper mapping with a later story
        newSegments: [],
        removedSegmentIds: originalAvailability
          .filter((segment) => !data.availability.find((s) => s.segmentId === segment.segmentId))
          .map((segment) => segment.segmentId),
      });
    }
  });

  const handleDeleteAll = formHandleSubmit(() => {
    updateWorkerAvailability({
      updatedSegments: [],
      removedSegmentIds: availabilitySegments.map((segment) => segment.segmentId),
      newSegments: [],
    });
  });

  return {
    handleSubmit,
    handleDeleteAll,
  };
};
